import { useEffect, useState } from 'react';

// A call in progress right now, from Tata's live view (GET /api/ringing-calls
// `live`, see server/tataLiveCalls.js): "Ringing — Sweta · 0:12",
// "On call — Sweta · 2:03". Ticks its own clock so only the badge re-renders
// every second, not the page around it.

const PHASE_LABELS = {
  ivr: () => 'In IVR',
  queue: () => 'Waiting for agent',
  ringing_agent: (agent) => `Ringing${agent ? ` — ${agent}` : ''}`,
  dialing_customer: (agent) => `Calling customer${agent ? ` — ${agent}` : ''}`,
  ringing_customer: (agent) => `Ringing customer${agent ? ` — ${agent}` : ''}`,
  connected: (agent) => `On call${agent ? ` — ${agent}` : ''}`,
};

export const liveCallLabel = (call) => (PHASE_LABELS[call.phase] || PHASE_LABELS.ivr)(call.agentName);

export const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

// One entry per customer number. If a number somehow has two live calls,
// the one where someone is talking wins, then the newest.
const talking = (call) => (call.phase === 'connected' ? 1 : 0);

export const indexLiveCalls = (list) => {
  const byNumber = new Map();
  for (const call of Array.isArray(list) ? list : []) {
    if (!call.customerNumber) continue;
    const current = byNumber.get(call.customerNumber);
    if (
      !current ||
      talking(call) > talking(current) ||
      (talking(call) === talking(current) && (call.startedAt || '') > (current.startedAt || ''))
    ) {
      byNumber.set(call.customerNumber, call);
    }
  }
  return byNumber;
};

// Milliseconds the browser's clock is ahead of the server's, from the
// response's serverTime, so a PC with a wrong clock still shows 0:05, not -3:55.
export const clockOffsetFrom = (serverTime) => {
  const server = Date.parse(serverTime || '');
  return Number.isNaN(server) ? 0 : Date.now() - server;
};

function LiveCallBadge({ call, clockOffsetMs = 0 }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const connected = call.phase === 'connected';
  // On a connected call the timer is talk time, from when the customer
  // picked up.
  const since = Date.parse((connected && call.connectedAt) || call.startedAt || '');
  const elapsed = Number.isNaN(since) ? '' : formatElapsed(now - clockOffsetMs - since);

  return (
    <span className={`status live-call ${connected ? 'live-call-connected' : 'live-call-ringing'}`}>
      <span className="live-call-dot" aria-hidden="true" />
      {liveCallLabel(call)}
      {elapsed && ` · ${elapsed}`}
    </span>
  );
}

export default LiveCallBadge;
